"use client";

import { useCallback, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import type { Dictionary } from "@/i18n/dictionaries";
import type { Locale } from "@/i18n/config";
import type { BranchOption } from "@/lib/data/booking";
import { branchName, studioName } from "@/lib/data/types";
import { setBookingSelection } from "@/lib/booking-selection";
import { addDaysISO, MIN_BOOKING_ADVANCE_DAYS } from "@/lib/availability";

export interface WidgetProductOption {
  id: string;
  name: string;
  durationMinutes: number;
  branchId?: string | null;
  minAdvanceDays?: number;
  maxAdvanceDays?: number | null;
  validFrom?: string | null;
  validTo?: string | null;
}

interface SlotDto {
  startMinute: number;
  label: string;
  available: boolean;
}

interface HeroBookingWidgetProps {
  locale: Locale;
  dict: Dictionary;
  products: WidgetProductOption[];
  branches: BranchOption[];
  defaultBranchId?: string;
  defaultStudioId?: string;
  todayISO: string;
}

/**
 * Hotel-style booking bar on the hero: pick program → date → live time
 * slots → jumps straight into the booking flow pre-filled.
 */
export function HeroBookingWidget({
  locale,
  dict,
  products,
  branches,
  defaultBranchId,
  defaultStudioId,
  todayISO,
}: HeroBookingWidgetProps) {
  const router = useRouter();
  const [productId, setProductId] = useState(products[0]?.id ?? "");

  const selectedProduct = useMemo(
    () => products.find((p) => p.id === productId) ?? products[0],
    [products, productId],
  );

  const { minDate, maxDate } = useMemo(() => {
    const minDays = selectedProduct?.minAdvanceDays ?? MIN_BOOKING_ADVANCE_DAYS;
    let minD = addDaysISO(todayISO, minDays);
    if (selectedProduct?.validFrom) {
      const validFromD = selectedProduct.validFrom.slice(0, 10);
      if (validFromD > minD) minD = validFromD;
    }

    const maxDays = selectedProduct?.maxAdvanceDays ?? 90;
    let maxD = addDaysISO(todayISO, maxDays);
    if (selectedProduct?.validTo) {
      const validToD = selectedProduct.validTo.slice(0, 10);
      if (validToD < maxD) maxD = validToD;
    }
    return { minDate: minD, maxDate: maxD };
  }, [selectedProduct, todayISO]);

  const [date, setDate] = useState("");
  const [slots, setSlots] = useState<SlotDto[] | null>(null);
  const [slotError, setSlotError] = useState(false);
  const [startMinute, setStartMinute] = useState<number | null>(null);
  const [branchId, setBranchId] = useState<string>(defaultBranchId ?? "");
  const [studioId, setStudioId] = useState<string>(defaultStudioId ?? "");
  const abortRef = useRef<AbortController | null>(null);

  const studiosForBranch = useMemo(
    () => branches.find((b) => b.id === branchId)?.studios ?? [],
    [branches, branchId],
  );

  const loadSlots = useCallback(
    async (targetDate: string, targetProduct: string) => {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(targetDate) || !targetProduct) return;
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const res = await fetch(
          `/api/availability?product=${encodeURIComponent(targetProduct)}&date=${targetDate}`,
          { signal: controller.signal },
        );
        if (!res.ok) throw new Error("fetch failed");
        const data = (await res.json()) as { slots: SlotDto[] };
        setSlots(data.slots);
        setSlotError(false);
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
        setSlots([]);
        setSlotError(true);
      }
    },
    [],
  );

  // Availability is re-queried from the change handlers below —
  // state transitions stay event-driven, no effects needed.
  const handleProductChange = (id: string) => {
    setProductId(id);
    setStartMinute(null);
    setSlots(null);
    const prod = products.find((p) => p.id === id);
    const minDays = prod?.minAdvanceDays ?? MIN_BOOKING_ADVANCE_DAYS;
    let minD = addDaysISO(todayISO, minDays);
    if (prod?.validFrom) {
      const validFromD = prod.validFrom.slice(0, 10);
      if (validFromD > minD) minD = validFromD;
    }
    const maxDays = prod?.maxAdvanceDays ?? 90;
    let maxD = addDaysISO(todayISO, maxDays);
    if (prod?.validTo) {
      const validToD = prod.validTo.slice(0, 10);
      if (validToD < maxD) maxD = validToD;
    }
    if (date && (date < minD || date > maxD)) {
      setDate("");
    } else if (date) {
      void loadSlots(date, id);
    }
  };

  const handleDateChange = (value: string) => {
    setDate(value);
    setStartMinute(null);
    setSlots(null);
    if (/^\d{4}-\d{2}-\d{2}$/.test(value) && value >= minDate && value <= maxDate) {
      void loadSlots(value, productId);
    }
  };

  const handleBranchChange = (id: string) => {
    setBranchId(id);
    setStudioId("");
    setDate("");
    setStartMinute(null);
    setSlots(null);
  };

  const handleStudioChange = (id: string) => {
    setStudioId(id);
    setDate("");
    setStartMinute(null);
    setSlots(null);
  };

  if (products.length === 0) return null;

  const availableSlots = (slots ?? []).filter((s) => s.available);

  const handleSubmit = () => {
    if (!date || startMinute === null || !productId) return;
    const params = new URLSearchParams({ date, start: String(startMinute) });
    if (studioId) params.set("studio", studioId);
    if (branchId) params.set("branch", branchId);
    setBookingSelection({ branchId, studioId });
    router.push(
      `/${locale}/booking/${productId}?${params.toString()}`,
    );
  };

  return (
    <div className="rounded-lg border border-line bg-paper p-5 text-left shadow-[0_36px_90px_-40px_rgba(35,33,29,0.35)] sm:p-6">
      <div className="mb-5 flex items-center justify-center gap-3" aria-hidden="true">
        <span className="h-px w-12 bg-line" />
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-muted"
        >
          <rect x="3" y="4" width="18" height="16" rx="1.5" />
          <path d="M8 2.5v3M16 2.5v3M3 9h18" />
          <path d="M9 14l2 2 4-4" />
        </svg>
        <span className="h-px w-12 bg-line" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:items-end">
        {/* Branch */}
        <div>
          <label htmlFor="hw-branch" className="field-label !mb-2">
            {dict.widget.branch}
          </label>
          <select
            id="hw-branch"
            className="field-input"
            value={branchId}
            onChange={(e) => handleBranchChange(e.target.value)}
          >
            <option value="">{dict.widget.selectBranch}</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {branchName(b, locale)}
              </option>
            ))}
          </select>
        </div>

        {/* Studio */}
        <div>
          <label htmlFor="hw-studio" className="field-label !mb-2">
            {dict.widget.studio}
          </label>
          <select
            id="hw-studio"
            className="field-input"
            value={studioId}
            disabled={!branchId}
            onChange={(e) => handleStudioChange(e.target.value)}
          >
            <option value="">
              {!branchId ? dict.widget.selectBranchFirst : dict.widget.selectStudio}
            </option>
            {studiosForBranch.map((s) => (
              <option key={s.id} value={s.id}>
                {studioName(s, locale)}
              </option>
            ))}
          </select>
        </div>

        {/* Program */}
        <div>
          <label htmlFor="hw-program" className="field-label !mb-2">
            {dict.widget.program}
          </label>
          <select
            id="hw-program"
            className="field-input"
            value={productId}
            disabled={!studioId}
            onChange={(e) => handleProductChange(e.target.value)}
          >
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} · {p.durationMinutes}
                {dict.common.min}
              </option>
            ))}
          </select>
        </div>

        {/* Date */}
        <div>
          <label htmlFor="hw-date" className="field-label !mb-2">
            {dict.widget.date}
          </label>
          <input
            id="hw-date"
            type="date"
            className="field-input"
            value={date}
            min={minDate}
            max={maxDate}
            disabled={!studioId}
            onChange={(e) => handleDateChange(e.target.value)}
          />
        </div>

        {/* Time */}
        <div>
          <label htmlFor="hw-time" className="field-label !mb-2">
            {dict.widget.time}
          </label>
          <select
            id="hw-time"
            className="field-input"
            value={startMinute ?? ""}
            disabled={!studioId || !date || availableSlots.length === 0}
            onChange={(e) =>
              setStartMinute(e.target.value === "" ? null : Number(e.target.value))
            }
          >
            <option value="">
              {!studioId
                ? dict.widget.selectStudioFirst
                : !date
                  ? dict.widget.selectDateFirst
                  : slots === null
                    ? dict.widget.timeLoading
                    : availableSlots.length === 0
                      ? dict.widget.noSlotsShort
                      : dict.widget.selectTime}
            </option>
            {availableSlots.map((slot) => (
              <option key={slot.startMinute} value={slot.startMinute}>
                {slot.label}
              </option>
            ))}
          </select>
        </div>

        {/* CTA */}
        <button
          type="button"
          className="btn-dark h-[46px] w-full px-7 lg:w-auto"
          onClick={handleSubmit}
          disabled={!date || startMinute === null}
          title={
            date && startMinute === null ? dict.widget.selectTimeFirst : undefined
          }
        >
          {dict.common.bookNow}
        </button>
      </div>

      {selectedProduct && (
        <p className="mt-4 text-xs leading-relaxed text-faint">
          {selectedProduct.durationMinutes}
          {dict.common.min} · {dict.widget.stepNote}
        </p>
      )}
      {slotError && (
        <p role="alert" className="mt-4 text-xs text-red-500">
          {dict.booking.errorGeneric}
        </p>
      )}
    </div>
  );
}
